import React, {
  useState,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormGroup,
  FormControlLabel,
  Switch
} from '@mui/material';
import { Formik, Form, FormikHelpers } from 'formik';
import * as yup from 'yup';
import FilePickerInput from '../../../../components/FilePickerInput';
import { ModalAlertData } from '../../../../redux/slices/mobileAppModalAlertSlice';
import { SelectOption } from '../../../../components/Select/interface';
import Select from '../../../../components/Select';

interface ModalAlertSettingsModalProps {
  onSave: (
    values: ModalAlertData,
    photo?: string | File,
    isEditMode?: boolean
  ) => void;
}

export interface ModalAlertSettingsModalHandles {
  openAddModal: () => void;
  openEditModal: (initialData: ModalAlertData) => void;
}

const defaultInitialData: ModalAlertData = {
  mainText: '',
  description: '',
  description2: '',
  photo: '',
  enabled: false
};

const validationSchema = yup.object({
  mainText: yup.string().required('Основний текст є обов’язковим'),
  description: yup.string().required('Опис є обов’язковим'),
  description2: yup.string().notRequired()
});

const appScreenOptions = [
  { label: 'Абонементи', value: 'Subscriptions' },
  { label: 'Промокоди', value: 'Promocodes' },
  { label: 'Гра', value: 'Game' },
  { label: 'Гаманець', value: 'Wallet' },
  { label: 'Бонуси', value: 'Bonuses' },
  { label: 'Колесо фортуни', value: 'BonusFortuneWeel' }
];


const ModalAlertSettingsModal = forwardRef<
  ModalAlertSettingsModalHandles,
  ModalAlertSettingsModalProps
>(({ onSave }, ref) => {
  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialData, setInitialData] = useState<ModalAlertData>(
    defaultInitialData
  );
  const [photoFile, setPhotoFile] = useState<File | undefined>();

  const citiesOptions = useMemo<SelectOption[]>(
    () => [
      {
        value: '',
        label: '-'
      },
      ...appScreenOptions
    ],
    []
  );

  useEffect(() => {
    if (!open) {
      setInitialData(defaultInitialData);
      setPhotoFile(undefined);
    }
  }, [open]);

  useImperativeHandle(ref, () => ({
    openAddModal() {
      setIsEditMode(false);
      setInitialData(defaultInitialData);
      setOpen(true);
    },
    openEditModal(data: ModalAlertData) {
      setIsEditMode(true);
      setInitialData(data);
      setOpen(true);
    }
  }));

  const handleClose = () => {
    setOpen(false);
  };

  const handleSubmit = (
    values: ModalAlertData,
    helpers: FormikHelpers<ModalAlertData>
  ) => {
    onSave(values, photoFile, isEditMode);
    setOpen(false);
    helpers.setSubmitting(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth='sm'>
      <DialogTitle>
        {isEditMode ? 'Редагувати алерт' : 'Додати алерт'}
      </DialogTitle>
      <Formik
        initialValues={initialData}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({
          values,
          handleChange,
          handleBlur,
          setFieldValue,
          isSubmitting,
          touched,
          errors
        }) => (
          <Form>
            <DialogContent>
              <TextField
                margin='dense'
                label='Основний текст'
                name='mainText'
                fullWidth
                variant='standard'
                value={values.mainText}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.mainText && errors.mainText)}
                helperText={errors.mainText}
              />
              <TextField
                margin='dense'
                label='Опис'
                name='description'
                fullWidth
                variant='standard'
                value={values.description}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.description && errors.description)}
                helperText={errors.description}
              />
              <Select
                fullWidth
                label='Екран докладніше'
                options={citiesOptions}
                onChange={value => setFieldValue('description2', value)}
                value={values.description2}
              />
              {/* <TextField
                margin='dense'
                label='Опис 2'
                name='description2'
                fullWidth
                variant='standard'
                value={values.description2}
                onChange={handleChange}
                onBlur={handleBlur}
                error={Boolean(touched.description2 && errors.description2)}
                helperText={errors.description2}
              /> */}

              <FilePickerInput
                label='Зображення'
                value={photoFile || undefined}
                onChange={(file: File) => {
                  setPhotoFile(file);
                }}
              />

              {isEditMode && (
                <FormGroup sx={{ marginTop: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.enabled}
                        onChange={event =>
                          setFieldValue('enabled', event.target.checked)
                        }
                      />
                    }
                    label='Активувати алерт'
                  />
                </FormGroup>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleClose}>Скасувати</Button>
              <Button type='submit' color='primary' disabled={isSubmitting}>
                Зберегти
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
});

export default ModalAlertSettingsModal;
